import { Component, OnInit } from '@angular/core';
import { IChecklistCategory, IChecklistSubCategory } from '../../interfaces/checklist-category';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ChecklistService } from '../../services/checklist.service';
import { compare } from 'fast-json-patch';
import { MessageService } from 'primeng';
import { Message } from '../../classes/Message';
import { ColorEvent } from 'ngx-color';

@Component({
  selector: 'checklist-categories',
  templateUrl: './checklist-categories.component.html',
  styleUrls: ['./checklist-categories.component.scss'],
})
export class ChecklistCategoriesComponent implements OnInit {
  subCategories: IChecklistSubCategory[];
  categories: IChecklistCategory[];
  showEditCategory: boolean = false;
  showEditSubCategory: boolean = false;
  categoryForm: UntypedFormGroup;
  subCategoryForm: UntypedFormGroup;
  saving: boolean = false;
  selectedCategory: IChecklistCategory;
  selectedSubCategory: IChecklistSubCategory;
  state: any;
  highlightColour: string = 'red';

  constructor(public checklistService: ChecklistService, public messageService: MessageService) {}

  ngOnInit() {
    this.initCategoryForm();
    this.initSubCategoryForm();
    this.getChecklistCategories();
  }

  getChecklistCategories() {
    this.checklistService
      .getChecklistCategories({ component: 'checklist-categories' })
      .then((result) => {
        this.categories = result;
      });
  }

  getChecklistSubCategories(id) {
    this.checklistService.getChecklistCategorySubCategories(id).then((result) => {
      this.subCategories = result;
    });
  }

  initCategoryForm() {
    this.categoryForm = new UntypedFormGroup({
      id: new UntypedFormControl(null),
      title: new UntypedFormControl('', Validators.compose([Validators.required])),
      rgb: new UntypedFormControl('', Validators.compose([Validators.required])),
      isActive: new UntypedFormControl(true),
    });
  }

  get f() {
    return this.categoryForm.controls;
  }

  initSubCategoryForm() {
    this.subCategoryForm = new UntypedFormGroup({
      id: new UntypedFormControl(null),
      checklistCategoryId: new UntypedFormControl(''),
      title: new UntypedFormControl('', Validators.compose([Validators.required])),
      description: new UntypedFormControl('', Validators.compose([Validators.required])),
      isActive: new UntypedFormControl(true),
      weighting: new UntypedFormControl(''),
      measureOnTimeOnCollection: new UntypedFormControl(true),
      measureOnTimeOnDelivery: new UntypedFormControl(true),
    });
  }

  addCategory() {
    this.initCategoryForm();
    this.showEditCategory = true;
  }

  addSubCategory() {
    this.initSubCategoryForm();
    this.subCategoryForm.patchValue({ checklistCategoryId: this.selectedCategory.id });
    this.showEditSubCategory = true;
  }

  async saveCategory() {
    await this.createUpdateCategory();

    this.saving = false;
    this.showEditCategory = false;

    this.getChecklistCategories();
  }

  async saveSubCategory() {
    await this.createUpdateSubCategory();

    this.saving = false;
    this.showEditSubCategory = false;
    this.getChecklistSubCategories(this.selectedCategory.id);
  }

  async createUpdateCategory() {
    let f = this.categoryForm.value;
    this.saving = true;

    let saveObject = <IChecklistCategory>{
      id: f.id,
      title: f.title,
      rgb: f.rgb,
      isActive: f.isActive,
    };

    if (f.id == null) {
      await this.checklistService
        .createChecklistCategory(saveObject)
        .then(() => {})
        .catch((err) => {
          this.messageService.add(
            Message.Error(
              'An error occurred creating the category. Ensure the category title is unique.',
              'Create Error',
            ),
          );
        });
    } else {
      await this.checklistService
        .updateChecklistCategory(this.categoryForm.value)
        .then(() => {})
        .catch((err) => {
          this.messageService.add(
            Message.Error(
              'An error occurred updating the category. Ensure the category title is unique',
              'Create Error',
            ),
          );
        });
    }
  }

  async createUpdateSubCategory() {
    let f = this.subCategoryForm.value;
    this.saving = true;

    let saveObject = <IChecklistSubCategory>{
      id: f.id,
      checklistCategoryId: f.checklistCategoryId,
      title: f.title,
      description: f.description,
      isActive: f.isActive,
      weighting: f.weighting,
      measureOnTimeOnCollection: f.measureOnTimeOnCollection,
      measureOnTimeOnDelivery: f.measureOnTimeOnDelivery,
    };

    if (f.id == null) {
      await this.checklistService
        .createChecklistSubCategory(saveObject)
        .then(() => {})
        .catch((err) => {
          this.messageService.add(
            Message.Error(
              'An error occurred creating the sub-category. Ensure the sub-category title is unique',
              'Create Error',
            ),
          );
        });
    } else {
      await this.checklistService
        .updateChecklistSubCategory(saveObject)
        .then(() => {})
        .catch((err) => {
          this.messageService.add(
            Message.Error(
              'An error occurred updating the sub-category. Ensure the sub-category title is unique',
              'Create Error',
            ),
          );
        });
    }
  }

  editCategory(category: IChecklistCategory) {
    this.categoryForm.patchValue(category);
    this.showEditCategory = true;
  }

  editSubCategory(subCategory: IChecklistSubCategory) {
    this.subCategoryForm.patchValue(subCategory);
    this.showEditSubCategory = true;
  }

  categoryChosen(data) {
    this.getChecklistSubCategories(data.id);
    this.selectedSubCategory = null;
  }

  changeComplete(event: ColorEvent) {
    this.highlightColour = event.color.hex;
    this.categoryForm.patchValue({ rgb: this.highlightColour });
  }
}
